/*
 * Copyright (c) 2022 CalAmp Corp. All Rights Reserved
 */
import React, {useEffect, useState} from 'react';
import {makeStyles} from '@material-ui/core';
import {useHasMounted} from '@ion/core/src/hooks/useHasMounted';
import {translate} from '@ion/core/src/locale/locale';
import {error} from '@ion/core/src/logger/logger';
import {getEvents} from '@ion/core/src/services/Connect';
import {GenericObject} from '@ion/core/src/types/entities';
import {sortByString} from '@ion/core/src/utils/ArrayUtil';
import Dropdown, {Options} from '@ion/dls/src/components/menus/Dropdown';
import {getOptions} from '@ion/features/src/helpers';

// defines the event type chooser prop definitions ...
export type EventTypeChooserProps = {
  onChange?: (event: GenericObject) => void;
  deviceId?: number;
  selectedValue?: GenericObject;
  readOnly?: boolean;
  required?: boolean;
  hasError?: boolean;
  classes?: string;
  dropDownOptions?: Options[];
  defaultValue?: string;
  displayLabel?: boolean;
  includeNone?: boolean;
  noneOptionValue?: any;
  noneOptionLabel?: string;
}

/**
 * Provides a utility which filters a list of Events for the
 * currently selected value.
 */
const getSelectedValue = (
  items: GenericObject[],
  selected: string
): GenericObject => {
  return items?.find(({name}: GenericObject) => name === selected);
};

// default styles used by the component when a classes prop is not provided ...
const useStyles = makeStyles(theme => ({
  root : {
    padding : '0px !important',
    margin  : '0px !important'
  }
}));

/**
 * The EventTypeChooser component displays a list of events
 * based on the provided deviceId, and allows for selecting
 * eventType from the list for subsequent use.
 *
 * ```typescript
 *
 *     import {EventTypeChooser} from '@ion/features/src/entities/events/EventTypeChooser';
 *
 *     // filter the list of events based on the given asset type and categories ...
 *     <EventTypeChooser onChange={setRole}
 *       deviceId={selectedAccount} />
 *
 * ```
 *
 * @param    deviceId The id of the Device for which events are to be loaded.
 * @param    selectedValue Selected eventType value on Editing
 * @param    onChange Callback prop which will be invoked with the selected eventType.
 * @param    readOnly When true, the component will be displayed in read - only mode;
 *           that is, disabled.Defaults to false.
 * @param    required When true, the required indicator will be displayed.
 * @paramha  hasError When true, the validationError indicator will be displayed.
 * @param    classes Optional classes to apply to the wrapping element.
 * @param    name name for the component that gets passed to the root element.
 */
export const EventTypeChooser = ({
  deviceId,
  onChange,
  selectedValue,
  defaultValue,
  readOnly,
  required,
  classes,
  includeNone,
  noneOptionValue,
  noneOptionLabel,
  dropDownOptions,
  hasError = false,
  displayLabel = true
}: EventTypeChooserProps) => {
  const _classes                  = useStyles({hasError});
  const [events, setEvents]       = useState<GenericObject[]>(null);
  const [isLoading, setIsLoading] = useState<boolean>(Boolean(deviceId));
  const [selected, setSelected]   = useState<string>(selectedValue?.name || '');
  const [options, setOptions]     = useState<Options[]>(dropDownOptions || []);
  const [open, setOpen]           = useState(false);
  const hasMounted                = useHasMounted();

  useEffect(() => {
    if (defaultValue && !selected) {
      setSelected(defaultValue);
    }
  }, [defaultValue]);

  useEffect(() => {
    // loading events based on selected device ...
    const loadEvents = async function() {
      let resp: GenericObject;
      setIsLoading(true);
      try {
        resp = await getEvents(deviceId);
      } catch (err) {
        error('Events: Error occurred retrieving Events');
        error(err);
      } finally {
        if (hasMounted !== false) {
          const countsArray = resp?.counts;
          const eventTypes = [];
          if (countsArray) {
            Object.keys(countsArray)?.forEach((key) => {
              const eventName       = key?.split(/(?=[A-Z])/).join(' ').replace('num', '');
              const eventFullName   = (eventName?.replace(/([A-Z])\s(?=[A-Z]\b)/g, '$1')).trim();
              const eventIdvalue    = eventName?.split(' ').join('').slice(0, -1);
              const finalEventValue = eventIdvalue?.charAt(0)?.toLowerCase() + eventIdvalue?.slice(1);
              const finalEventName  = eventFullName?.trim();
              eventTypes.push({id: finalEventValue, name: finalEventName});
            });
          }
          setEvents(eventTypes);
          setOptions(getOptions(sortByString(eventTypes, 'name')));
          setIsLoading(false);
        }
      }
    };
    if (deviceId) {
      loadEvents();
    }
  }, [deviceId]);

  return (
    <div className={classes || _classes.root}>
      <Dropdown displaySelectedLabel
        required={required}
        disabled={readOnly || isLoading}
        hasError={hasError}
        errorText={translate('devicefiles:features.eventType.error')}
        options={options}
        loading={isLoading}
        includeNone={includeNone}
        noneOptionValue={noneOptionValue}
        noneOptionLabel={noneOptionLabel}
        value={!isLoading ? selected : translate('common:states.loading')}
        fieldLabel={displayLabel ? translate('common:features.eventType.label') : ''}
        placeholder={translate('common:features.eventType.placeholder')}
        preferPlaceholder={!isLoading && !selected}
        onChange={onChange ? function({target}) {
          const name = target.value as unknown as string;
          setSelected(name);
          onChange(getSelectedValue(events || dropDownOptions, name));
        } : null} />
    </div>
  );
};

export default EventTypeChooser;
